import { useCallback, useEffect, useRef, useState } from 'react';
import {
  BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE,
  acquireBookingCheckoutLock,
  clearBookingPaymentDraft,
  isAuthorizedPaymentStatus,
  isProcessingPaymentStatus,
  loadBookingPaymentDraft,
  releaseBookingCheckoutLock,
  resolveAuthorizedPayment,
  resolveExistingAuthorizedPayment,
  saveBookingPaymentDraft,
  shouldClearPaymentDraftAfterError,
  shouldClearPaymentDraftForStatus,
  startBookingCheckoutLockRefresh,
} from '../../util/stripeBookingPaymentFlow';
import {
  getReservationStripeRouteMismatch,
  STRIPE_ROUTE_CHANGED_MESSAGE,
} from '../../util/stripeReservationRoute';
import { isBookingAuthError } from '../../util/bookingAuthRedirect';
import { logBookingDiagnostic } from '../../util/bookingDiagnostics';
import { trackPaymentInfoAdded } from '../../util/tracking';

export const PREPARED_PAYMENT_EXPIRY_MESSAGE =
  'Die Zahlungsprüfung ist abgelaufen. Bitte prüfe die Buchung erneut.';

const PAYMENT_CONFIRM_SAFETY_MARGIN_SECONDS = 90;

const createUserFacingError = (message) => {
  const error = new Error(message);
  error.userFacing = true;
  return error;
};

const getUserFacingErrorMessage = (error, fallbackMessage) => {
  if (error?.userFacing || error?.message === STRIPE_ROUTE_CHANGED_MESSAGE) {
    return error.message;
  }
  return fallbackMessage;
};

const getNowEpochSeconds = () => Math.floor(Date.now() / 1000);

const isReservationSafeForPayment = (reservation) => {
  const expiresAt = Number(reservation?.expiresAt);
  return Number.isFinite(expiresAt)
    && expiresAt - getNowEpochSeconds() > PAYMENT_CONFIRM_SAFETY_MARGIN_SECONDS;
};

export const usePreparedBookingPaymentFlow = ({
  autoPrepare = false,
  canPreparePayment,
  validateProjectFields,
  buildBookingPayload,
  buildPayloadSignature,
  runSlotReservation,
  finalizeBookingAfterAuthorizedPayment,
  setBookingError,
  setPaymentProcessing,
  paymentProcessing,
  loading,
  canUseConnect,
  stripeConnectAccountId,
  onReservationStripeRoute,
  ensureReservationAmountReady,
  reservationCacheRef,
  onAuthRequired,
  diagnosticLabel,
  source,
  messages,
}) => {
  const [status, setStatus] = useState('idle');
  const [preparedPayment, setPreparedPayment] = useState(null);
  const [autoPrepareAttempted, setAutoPrepareAttempted] = useState(false);
  const preparedPaymentRef = useRef(null);
  const prepareInFlightRef = useRef(false);
  const confirmInFlightRef = useRef(false);
  const resumeAttemptedRef = useRef(false);
  const autoPrepareAttemptedRef = useRef(false);
  const checkoutLockRef = useRef(null);
  const stopCheckoutLockRefreshRef = useRef(null);
  const trackedPaymentInfoRef = useRef(new Set());

  const setPreparedPaymentState = useCallback((nextPreparedPayment) => {
    preparedPaymentRef.current = nextPreparedPayment;
    setPreparedPayment(nextPreparedPayment);
  }, []);

  const releaseActiveCheckoutLock = useCallback((payloadOverride = null) => {
    stopCheckoutLockRefreshRef.current?.();
    stopCheckoutLockRefreshRef.current = null;

    const lock = checkoutLockRef.current;
    const studioID = payloadOverride?.studioID || lock?.studioID;
    if (lock?.acquired && studioID) {
      releaseBookingCheckoutLock({
        studioID,
        ownerID: lock.ownerID,
      });
    }
    checkoutLockRef.current = null;
  }, []);

  useEffect(() => () => {
    releaseActiveCheckoutLock();
  }, [releaseActiveCheckoutLock]);

  const ensureCheckoutLock = useCallback((payload, payloadSignature) => {
    const existingLock = checkoutLockRef.current;
    if (
      existingLock?.acquired
      && existingLock.studioID === payload.studioID
      && existingLock.payloadSignature === payloadSignature
    ) {
      return existingLock;
    }

    releaseActiveCheckoutLock(payload);

    const checkoutLock = acquireBookingCheckoutLock({
      studioID: payload.studioID,
      payloadSignature,
    });

    if (!checkoutLock.acquired) {
      return checkoutLock;
    }

    checkoutLockRef.current = {
      ...checkoutLock,
      studioID: payload.studioID,
      payloadSignature,
    };
    stopCheckoutLockRefreshRef.current = startBookingCheckoutLockRefresh({
      studioID: payload.studioID,
      ownerID: checkoutLock.ownerID,
    });

    return checkoutLockRef.current;
  }, [releaseActiveCheckoutLock]);

  const clearRememberedReservation = useCallback((reservation = null) => {
    if (!reservationCacheRef?.current) return;
    if (!reservation || reservationCacheRef.current.reservation?.slotID === reservation?.slotID) {
      reservationCacheRef.current = null;
    }
  }, [reservationCacheRef]);

  const rememberReservation = useCallback(({ payloadSignature, reservation }) => {
    if (!reservationCacheRef) return;
    reservationCacheRef.current = {
      payloadSignature,
      reservation,
    };
  }, [reservationCacheRef]);

  const getCachedReservation = useCallback((payloadSignature) => {
    const cached = reservationCacheRef?.current;
    if (!cached || cached.payloadSignature !== payloadSignature) {
      return null;
    }

    const expiresAt = Number(cached.reservation?.expiresAt);
    if (Number.isFinite(expiresAt) && expiresAt <= getNowEpochSeconds()) {
      reservationCacheRef.current = null;
      return null;
    }

    return cached.reservation || null;
  }, [reservationCacheRef]);

  const invalidatePreparedReservation = useCallback(({
    message = null,
    clearDraft = true,
    releaseLock = true,
    nextStatus = 'idle',
  } = {}) => {
    const current = preparedPaymentRef.current;
    if (clearDraft && current?.payload?.studioID) {
      clearBookingPaymentDraft(current.payload.studioID);
    }
    clearRememberedReservation(current?.reservation || null);
    setPreparedPaymentState(null);
    setStatus(nextStatus);
    if (message) {
      setBookingError(message);
    }
    if (releaseLock) {
      releaseActiveCheckoutLock(current?.payload || null);
    }
  }, [
    clearRememberedReservation,
    releaseActiveCheckoutLock,
    setBookingError,
    setPreparedPaymentState,
  ]);

  const syncReservationRouteForPreparedIntent = useCallback((reservation) => {
    const mismatch = getReservationStripeRouteMismatch({
      reservation,
      canUseConnect,
      stripeConnectAccountId,
    });

    if (mismatch.matches) {
      return true;
    }

    if (mismatch.routeMismatch || mismatch.accountMismatch) {
      onReservationStripeRoute?.(reservation);
      return true;
    }

    clearRememberedReservation(reservation);
    throw new Error(STRIPE_ROUTE_CHANGED_MESSAGE);
  }, [
    canUseConnect,
    stripeConnectAccountId,
    onReservationStripeRoute,
    clearRememberedReservation,
  ]);

  const trackPaymentInfoOnce = useCallback((reservation, payload) => {
    const key = reservation?.paymentIntentID || reservation?.slotID;
    if (!key || trackedPaymentInfoRef.current.has(key)) return;
    trackedPaymentInfoRef.current.add(key);
    trackPaymentInfoAdded({
      payload,
      reservation,
      source,
    });
  }, [source]);

  const buildCurrentPayloadWithSignature = useCallback(() => {
    const payload = buildBookingPayload();
    return {
      payload,
      payloadSignature: buildPayloadSignature(payload),
    };
  }, [buildBookingPayload, buildPayloadSignature]);

  const storePreparedReservation = useCallback(({
    payload,
    payloadSignature,
    reservation,
    shouldResume = false,
  }) => {
    saveBookingPaymentDraft({
      studioID: payload.studioID,
      payloadSignature,
      reservation,
    });
    rememberReservation({ payloadSignature, reservation });
    resumeAttemptedRef.current = false;
    setPreparedPaymentState({
      payload,
      payloadSignature,
      reservation,
      shouldResume,
    });
    setStatus('prepared');
  }, [rememberReservation, setPreparedPaymentState]);

  const prepareBookingPayment = useCallback(async () => {
    if (!canPreparePayment || prepareInFlightRef.current || confirmInFlightRef.current || paymentProcessing || loading) {
      return false;
    }

    setBookingError(null);
    if (!validateProjectFields()) {
      setStatus('error');
      return false;
    }

    let payload;
    let payloadSignature;
    try {
      ({ payload, payloadSignature } = buildCurrentPayloadWithSignature());
    } catch (payloadError) {
      setStatus('error');
      setBookingError(getUserFacingErrorMessage(payloadError, messages.bookingPreparationError));
      return false;
    }

    const checkoutLock = ensureCheckoutLock(payload, payloadSignature);
    if (!checkoutLock.acquired) {
      setStatus('error');
      setBookingError(BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE);
      return false;
    }

    prepareInFlightRef.current = true;
    setStatus('preparing');
    setPaymentProcessing(true);

    try {
      let shouldResume = false;
      let reservation = getCachedReservation(payloadSignature);
      if (!reservation) {
        reservation = loadBookingPaymentDraft({
          studioID: payload.studioID,
          payloadSignature,
        });
        shouldResume = Boolean(reservation);
      }

      if (!reservation) {
        try {
          reservation = await runSlotReservation(payload);
        } catch (reservationError) {
          logBookingDiagnostic(diagnosticLabel, 'slot reservation failed', { error: reservationError });
          if (isBookingAuthError(reservationError)) {
            onAuthRequired?.(payload);
            releaseActiveCheckoutLock(payload);
            setStatus('idle');
            return false;
          }
          throw createUserFacingError(messages.bookingPreparationError);
        }
      }

      if (!reservation?.clientSecret || !reservation?.slotID || !reservation?.paymentIntentID) {
        throw createUserFacingError(messages.bookingPreparationError);
      }

      saveBookingPaymentDraft({
        studioID: payload.studioID,
        payloadSignature,
        reservation,
      });
      rememberReservation({ payloadSignature, reservation });
      syncReservationRouteForPreparedIntent(reservation);

      if (!ensureReservationAmountReady(reservation)) {
        setStatus('error');
        return false;
      }

      storePreparedReservation({
        payload,
        payloadSignature,
        reservation,
        shouldResume,
      });
      return true;
    } catch (err) {
      if (err?.message === STRIPE_ROUTE_CHANGED_MESSAGE && payload?.studioID) {
        clearBookingPaymentDraft(payload.studioID);
        clearRememberedReservation();
      } else if (shouldClearPaymentDraftAfterError(err) && payload?.studioID) {
        clearBookingPaymentDraft(payload.studioID);
        clearRememberedReservation();
      }
      releaseActiveCheckoutLock(payload);
      setPreparedPaymentState(null);
      setStatus('error');
      setBookingError(getUserFacingErrorMessage(err, messages.bookingPreparationError));
      logBookingDiagnostic(diagnosticLabel, 'payment preparation error', { error: err });
      return false;
    } finally {
      setPaymentProcessing(false);
      prepareInFlightRef.current = false;
    }
  }, [
    canPreparePayment,
    paymentProcessing,
    loading,
    setBookingError,
    validateProjectFields,
    buildCurrentPayloadWithSignature,
    ensureCheckoutLock,
    setPaymentProcessing,
    getCachedReservation,
    runSlotReservation,
    diagnosticLabel,
    onAuthRequired,
    releaseActiveCheckoutLock,
    messages.bookingPreparationError,
    rememberReservation,
    syncReservationRouteForPreparedIntent,
    ensureReservationAmountReady,
    storePreparedReservation,
    clearRememberedReservation,
    setPreparedPaymentState,
  ]);

  useEffect(() => {
    if (!autoPrepare || !canPreparePayment || preparedPayment || status !== 'idle') {
      return;
    }

    if (autoPrepareAttemptedRef.current) {
      return;
    }
    autoPrepareAttemptedRef.current = true;
    setAutoPrepareAttempted(true);

    prepareBookingPayment();
  }, [
    autoPrepare,
    canPreparePayment,
    preparedPayment,
    status,
    prepareBookingPayment,
  ]);

  useEffect(() => {
    if (preparedPayment || !canPreparePayment || status !== 'idle') {
      return;
    }

    let payload;
    let payloadSignature;
    try {
      ({ payload, payloadSignature } = buildCurrentPayloadWithSignature());
    } catch (payloadError) {
      return;
    }

    const reservation = getCachedReservation(payloadSignature) || loadBookingPaymentDraft({
      studioID: payload.studioID,
      payloadSignature,
    });
    if (!reservation) return;

    rememberReservation({ payloadSignature, reservation });
    try {
      syncReservationRouteForPreparedIntent(reservation);
      if (!ensureReservationAmountReady(reservation)) {
        setStatus('error');
        return;
      }
      const checkoutLock = ensureCheckoutLock(payload, payloadSignature);
      if (!checkoutLock.acquired) {
        setStatus('error');
        setBookingError(BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE);
        return;
      }
      storePreparedReservation({
        payload,
        payloadSignature,
        reservation,
        shouldResume: true,
      });
    } catch (err) {
      if (payload?.studioID) {
        clearBookingPaymentDraft(payload.studioID);
      }
      clearRememberedReservation(reservation);
      setStatus('error');
      setBookingError(getUserFacingErrorMessage(err, messages.paymentConfirmationError));
    }
  }, [
    preparedPayment,
    canPreparePayment,
    status,
    buildCurrentPayloadWithSignature,
    getCachedReservation,
    rememberReservation,
    syncReservationRouteForPreparedIntent,
    ensureReservationAmountReady,
    ensureCheckoutLock,
    storePreparedReservation,
    clearRememberedReservation,
    setBookingError,
    messages.paymentConfirmationError,
  ]);

  useEffect(() => {
    if (!preparedPayment) return;

    let currentPayloadSignature;
    try {
      currentPayloadSignature = buildPayloadSignature(buildBookingPayload());
    } catch (payloadError) {
      invalidatePreparedReservation({ clearDraft: true });
      return;
    }

    if (currentPayloadSignature !== preparedPayment.payloadSignature) {
      invalidatePreparedReservation({ clearDraft: true });
    }
  }, [
    preparedPayment,
    buildBookingPayload,
    buildPayloadSignature,
    invalidatePreparedReservation,
  ]);

  useEffect(() => {
    const reservation = preparedPayment?.reservation;
    if (!reservation) return undefined;

    const expiresAt = Number(reservation.expiresAt);
    if (!Number.isFinite(expiresAt)) return undefined;

    const delayMs = Math.max(
      (expiresAt - PAYMENT_CONFIRM_SAFETY_MARGIN_SECONDS - getNowEpochSeconds()) * 1000,
      0
    );

    const timeoutID = window.setTimeout(() => {
      invalidatePreparedReservation({
        message: PREPARED_PAYMENT_EXPIRY_MESSAGE,
        clearDraft: true,
        nextStatus: 'expired',
      });
    }, delayMs);

    return () => window.clearTimeout(timeoutID);
  }, [preparedPayment?.reservation, invalidatePreparedReservation]);

  const resumePreparedPayment = useCallback(async ({ stripe }) => {
    const current = preparedPaymentRef.current;
    if (
      !stripe
      || !current
      || !current.shouldResume
      || resumeAttemptedRef.current
      || confirmInFlightRef.current
      || prepareInFlightRef.current
    ) {
      return;
    }

    resumeAttemptedRef.current = true;

    const checkoutLock = ensureCheckoutLock(current.payload, current.payloadSignature);
    if (!checkoutLock.acquired) {
      setBookingError(BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE);
      return;
    }

    confirmInFlightRef.current = true;
    setPaymentProcessing(true);

    try {
      const currentResult = await stripe.retrievePaymentIntent(current.reservation.clientSecret);
      if (currentResult?.error) {
        throw currentResult.error;
      }

      const currentIntent = currentResult?.paymentIntent;
      if (shouldClearPaymentDraftForStatus(currentIntent?.status)) {
        clearBookingPaymentDraft(current.payload.studioID);
        clearRememberedReservation(current.reservation);
        setPreparedPaymentState(null);
        releaseActiveCheckoutLock(current.payload);
        setStatus('idle');
        return;
      }

      if (
        isAuthorizedPaymentStatus(currentIntent?.status)
        || isProcessingPaymentStatus(currentIntent?.status)
      ) {
        setBookingError(null);
        await resolveExistingAuthorizedPayment({
          stripe,
          clientSecret: current.reservation.clientSecret,
          initialPaymentIntent: currentIntent,
        });
        trackPaymentInfoOnce(current.reservation, current.payload);
        await finalizeBookingAfterAuthorizedPayment(current.reservation, current.payload);
        clearBookingPaymentDraft(current.payload.studioID);
        clearRememberedReservation(current.reservation);
        setPreparedPaymentState(null);
        releaseActiveCheckoutLock(current.payload);
        setStatus('idle');
      }
    } catch (err) {
      if (err?.message === STRIPE_ROUTE_CHANGED_MESSAGE && current.payload?.studioID) {
        clearBookingPaymentDraft(current.payload.studioID);
        clearRememberedReservation();
        setPreparedPaymentState(null);
      } else if (shouldClearPaymentDraftAfterError(err) && current.payload?.studioID) {
        clearBookingPaymentDraft(current.payload.studioID);
        clearRememberedReservation();
        setPreparedPaymentState(null);
      }
      setBookingError(getUserFacingErrorMessage(err, messages.paymentConfirmationError));
      logBookingDiagnostic(diagnosticLabel, 'payment draft resume error', { error: err });
    } finally {
      setPaymentProcessing(false);
      confirmInFlightRef.current = false;
    }
  }, [
    ensureCheckoutLock,
    setBookingError,
    setPaymentProcessing,
    clearRememberedReservation,
    setPreparedPaymentState,
    releaseActiveCheckoutLock,
    trackPaymentInfoOnce,
    finalizeBookingAfterAuthorizedPayment,
    messages.paymentConfirmationError,
    diagnosticLabel,
  ]);

  const confirmPreparedPayment = useCallback(async ({
    stripe,
    elements,
    cardElement = null,
    submitBeforeConfirm = false,
    expressEvent = null,
    requirePaymentElementReady = false,
    paymentElementReady = true,
    paymentElementLoadError = null,
  }) => {
    const current = preparedPaymentRef.current;
    if (!stripe || !elements || !current) return false;
    if (requirePaymentElementReady && (!paymentElementReady || paymentElementLoadError)) return false;
    if (confirmInFlightRef.current || prepareInFlightRef.current || paymentProcessing || loading) return false;

    confirmInFlightRef.current = true;
    setPaymentProcessing(true);
    setStatus('confirming');
    setBookingError(null);

    try {
      if (!validateProjectFields()) {
        throw createUserFacingError(messages.paymentDetailsError);
      }

      const { payload, payloadSignature } = buildCurrentPayloadWithSignature();
      if (payloadSignature !== current.payloadSignature) {
        invalidatePreparedReservation({ clearDraft: true });
        throw createUserFacingError(PREPARED_PAYMENT_EXPIRY_MESSAGE);
      }

      if (!isReservationSafeForPayment(current.reservation)) {
        invalidatePreparedReservation({
          message: PREPARED_PAYMENT_EXPIRY_MESSAGE,
          clearDraft: true,
          nextStatus: 'expired',
        });
        throw createUserFacingError(PREPARED_PAYMENT_EXPIRY_MESSAGE);
      }

      const checkoutLock = ensureCheckoutLock(payload, payloadSignature);
      if (!checkoutLock.acquired) {
        throw createUserFacingError(BOOKING_CHECKOUT_IN_PROGRESS_MESSAGE);
      }

      if (submitBeforeConfirm) {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          logBookingDiagnostic(diagnosticLabel, 'elements.submit failed', { error: submitError });
          throw createUserFacingError(messages.paymentDetailsError);
        }
      }

      if (expressEvent) {
        if (typeof stripe.confirmPayment !== 'function') {
          throw createUserFacingError(messages.paymentConfirmationError);
        }

        await resolveAuthorizedPayment({
          stripe,
          clientSecret: current.reservation.clientSecret,
          confirmPayment: () => stripe.confirmPayment({
            elements,
            clientSecret: current.reservation.clientSecret,
            confirmParams: {
              return_url: window.location.href,
            },
            redirect: 'if_required',
          }),
        });
      } else {
        if (!cardElement) {
          throw createUserFacingError(messages.paymentDetailsError);
        }

        if (typeof stripe.confirmCardPayment !== 'function') {
          throw createUserFacingError(messages.paymentConfirmationError);
        }

        await resolveAuthorizedPayment({
          stripe,
          clientSecret: current.reservation.clientSecret,
          confirmPayment: () => stripe.confirmCardPayment(
            current.reservation.clientSecret,
            {
              payment_method: {
                card: cardElement,
                billing_details: {},
              },
              return_url: window.location.href,
            },
          ),
        });
      }

      setStatus('finalizing');
      trackPaymentInfoOnce(current.reservation, payload);
      await finalizeBookingAfterAuthorizedPayment(current.reservation, payload);
      clearBookingPaymentDraft(payload.studioID);
      clearRememberedReservation(current.reservation);
      setPreparedPaymentState(null);
      releaseActiveCheckoutLock(payload);
      setStatus('idle');
      return true;
    } catch (err) {
      expressEvent?.paymentFailed?.({ reason: 'fail' });
      const currentAfterError = preparedPaymentRef.current || current;
      if (err?.message === STRIPE_ROUTE_CHANGED_MESSAGE && currentAfterError?.payload?.studioID) {
        clearBookingPaymentDraft(currentAfterError.payload.studioID);
        clearRememberedReservation();
        setPreparedPaymentState(null);
      } else if (shouldClearPaymentDraftAfterError(err) && currentAfterError?.payload?.studioID) {
        clearBookingPaymentDraft(currentAfterError.payload.studioID);
        clearRememberedReservation();
        setPreparedPaymentState(null);
      }
      setStatus(
        err?.message === PREPARED_PAYMENT_EXPIRY_MESSAGE && !preparedPaymentRef.current
          ? 'expired'
          : preparedPaymentRef.current
            ? 'prepared'
            : 'error'
      );
      setBookingError(getUserFacingErrorMessage(err, messages.paymentConfirmationError));
      logBookingDiagnostic(diagnosticLabel, 'Pay chain error', { error: err });
      return false;
    } finally {
      setPaymentProcessing(false);
      confirmInFlightRef.current = false;
    }
  }, [
    paymentProcessing,
    loading,
    setPaymentProcessing,
    setBookingError,
    validateProjectFields,
    messages.paymentDetailsError,
    messages.paymentConfirmationError,
    buildCurrentPayloadWithSignature,
    invalidatePreparedReservation,
    ensureCheckoutLock,
    diagnosticLabel,
    trackPaymentInfoOnce,
    finalizeBookingAfterAuthorizedPayment,
    clearRememberedReservation,
    setPreparedPaymentState,
    releaseActiveCheckoutLock,
  ]);

  return {
    status,
    preparedPayment,
    autoPrepareAttempted,
    prepareBookingPayment,
    invalidatePreparedReservation,
    confirmPreparedPayment,
    resumePreparedPayment,
  };
};
